import { inject, injectable } from 'inversify';

import { TYPES } from '@/engine/ioc';
import { CORE_TYPES } from '@/engine/app/modules/core';

import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import {
  BoxesController,
  type IBaseViewModel,
  type IViewItemsViewModelController,
  SceneItemAction
} from '@/engine/app/interfaces';
import type { IBoxViewModelModifier } from '@/components/boxes/common/interfaces.ts';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import type { IValue } from '@/engine/DataFlow/Graph';
import { TYPES as INDTYPES } from '@/engine/app/modules/indications/iocTypes';
import type { IRemoveCommandsFactory } from '@/engine/app/modules/indications/IndicationRemoveHighlighter';
import { WithMixed, WithUndefined } from '@/components/viewOption/types';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';

@injectable()
export class IndicationsBoxController implements BoxesController {
  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viVmController: IViewItemsViewModelController;

  private readonly _vmModifier: IBoxViewModelModifier;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _removeCommandsFactory: IRemoveCommandsFactory;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IViewItemsViewModelController) viVmController: IViewItemsViewModelController,
    @inject(TYPES.IBoxViewModelModifier) vmModifier: IBoxViewModelModifier,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor,
    @inject(INDTYPES.RemoveIndicationCommandsFactory) removeCommandsFactory: IRemoveCommandsFactory
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._viVmController = viVmController;
    this._vmModifier = vmModifier;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;
    this._removeCommandsFactory = removeCommandsFactory;
  }

  public async deleteIndication(viewModel: IBaseViewModel): Promise<void> {
    const command: ICommand = this._removeCommandsFactory.createCommand(viewModel.kind, viewModel);
    await this._commandProcessor.execute(command);

    this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationDeleted, viewModel);
  }

  public setIndicationSelection(descriptor: string, isSelected: boolean): void {
    this._vmModifier.setViewModelSelection(descriptor, isSelected);
  }

  private _findIndication(descriptor: string): IBaseViewModel | undefined {
    return this._viewsService.currentView?.getMeasurements().find((meas) => meas.descriptor == descriptor);
  }

  public setIndicationVisibility(descriptor: string, isVisible: boolean): void {
    this._vmModifier.setViewModelVisibility(descriptor, isVisible);

    this._viewsService.currentView
      ?.getObjects()
      .find((o) => o.attachments.at(0) === descriptor)
      ?.setVisibility(isVisible);

    const vm = this._findIndication(descriptor);
    if (vm) {
      this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationVisibilityChange, vm);
    }
  }

  public updateAllIndicationsVisibility(descriptors: Array<string>, isVisible: boolean): void {
    descriptors.forEach((d) => this.setIndicationVisibility(d, isVisible));
  }

  public checkGroupViewItemsBeingEdited(descriptors: Array<string>): WithUndefined<WithMixed<boolean>> {
    if (descriptors.length === 0) {
      return undefined;
    }

    const allBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, true) !== false);
    const allNotBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, true) !== true);

    if (allBeingEdited) {
      return true;
    }
    if (allNotBeingEdited) {
      return false;
    }
    return 'mixed';
  }

  public updateAllViewItemsBeingEdited(descriptors: Array<string>, isBeingEdited: boolean): void {
    descriptors.forEach((descriptor) => {
      this.setIndicationBeingEdited(descriptor, isBeingEdited);
    });
  }

  public setIndicationBeingEdited(descriptor: string, isBeingEdited: boolean): void {
    this._viVmController.setViewModelBeingEdited(descriptor, isBeingEdited, true);
    const identifier = `isBeingEdited$${descriptor}`;
    this._viewsService.getCurrentViewLifetime().remove(identifier);
    if (isBeingEdited) {
      this._viewsService
        .getCurrentViewLifetime()
        .set(identifier, new AutoDisposer(() => this._viVmController.setViewModelBeingEdited(descriptor, false, true)));
    }
  }

  public isIndicationBeingEdited(descriptor: string): boolean {
    return this._viVmController.isViewModelBeingEdited(descriptor, true) === true;
  }

  public setLabel(descriptor: string, label: string): void {
    const currentView = this._viewsService.currentView;

    if (!currentView) throw new Error('View should be defined');

    this._viewsService.views.forEach((view) => {
      const vM: IBaseViewModel | undefined = view.getItemByDescriptor(descriptor);
      vM?.setLabel(label);
    });

    const vm = currentView.getItemByDescriptor(descriptor);

    if (!vm) throw new Error('VM should be defined');

    this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationLabelEdited, vm);
  }

  public isDefaultAsync(descriptor: string): Promise<boolean> {
    return this._planningAlgorithm.areTuneValuesDefaultAsync([descriptor]);
  }

  public async resetToDefaultAsync(descriptor: string): Promise<void> {
    const valueToUndo: IValue | undefined = await this._planningAlgorithm.getIValueAsync(descriptor);
    if (valueToUndo === undefined) {
      throw new Error('Undo value is undefined');
    }

    await this._commandProcessor.executeAnonymousCommand(
      async (): Promise<undefined> => {
        await this._planningAlgorithm.resetTuneValuesAsync([descriptor]);
      },
      async (): Promise<undefined> => {
        await this._planningAlgorithm.setTuneValuesAsync([[descriptor, valueToUndo]]);
      }
    );
  }
}
